import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { userAPI } from '../services/api';
import { showError, showLoading, updateToSuccess, updateToError } from '../utils/toast';
import { FaArrowLeft, FaEnvelope, FaReply, FaClock, FaCheckCircle, FaCar, FaBell, FaComments, FaComment } from 'react-icons/fa';
import DriverChat from '../components/DriverChat';

const UserMessages = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeBookings, setActiveBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'messages', 'notifications', 'driver-chat'
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showDriverChat, setShowDriverChat] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showSendMessageModal, setShowSendMessageModal] = useState(false);
  const [messageToDriver, setMessageToDriver] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([fetchMessages(), fetchNotifications(), fetchActiveBookings()]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const fetchMessages = async () => {
    try {
      const response = await userAPI.getMessages();
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      showError(t('messages.failed_to_fetch', '❌ Failed to load messages'));
    }
  };

  const fetchNotifications = async () => {
    try {
      // Get user email from localStorage or context
      const userEmail = localStorage.getItem('userEmail') || 
                       JSON.parse(localStorage.getItem('user') || '{}').email;
      
      if (userEmail) {
        const response = await userAPI.getNotifications(userEmail);
        setNotifications(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Don't show error for notifications as they're secondary
    }
  };

  const fetchActiveBookings = async () => {
    try {
      const response = await userAPI.getActiveBookings();
      setActiveBookings(response.data || []);
    } catch (error) {
      console.error('Error fetching active bookings:', error);
      // If endpoint doesn't exist (404), set empty array and don't show error
      if (error.response?.status === 404) {
        console.warn('Active bookings endpoint not implemented yet');
        setActiveBookings([]);
      } else {
        // For other errors, still set empty array to prevent UI issues
        setActiveBookings([]);
      }
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedMessage) return;

    const loadingToast = showLoading(t('messages.reply.sending', 'Sending reply...'));
    
    try {
      // Get user data from localStorage or use defaults
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const userEmail = localStorage.getItem('userEmail') || userData.email;
      const userName = userData.name || userData.username;
      const authToken = localStorage.getItem('authToken');
      
      console.log('User authentication status:', {
        hasUserData: Object.keys(userData).length > 0,
        userEmail,
        userName,
        hasAuthToken: !!authToken
      });
      
      // Check if user is authenticated
      if (!userEmail || !userName) {
        updateToError(loadingToast, 'Please log in to send replies.');
        return;
      }
      
      // Map subject to valid backend choices
      const getValidSubject = (originalSubject) => {
        const subjectLower = originalSubject.toLowerCase();
        if (subjectLower.includes('booking') || subjectLower.includes('reservation')) return 'booking';
        if (subjectLower.includes('support') || subjectLower.includes('help') || subjectLower.includes('technical')) return 'support';
        if (subjectLower.includes('feedback') || subjectLower.includes('review')) return 'feedback';
        if (subjectLower.includes('complaint') || subjectLower.includes('problem') || subjectLower.includes('issue')) return 'complaint';
        return 'other'; // Default fallback
      };
      
      // Try different data structures based on whether this is a reply or new message
      const replyData = {
        name: userName,
        email: userEmail,
        phone: userData.phone || '',
        subject: getValidSubject(selectedMessage.subject), // Use valid subject choice
        message: `Reply to: ${selectedMessage.subject}\n\n${replyText.trim()}`, // Include original subject in message
        // Add additional fields that might be expected
        ...(selectedMessage.id && { parent_message_id: selectedMessage.id }),
        ...(selectedMessage.id && { original_message_id: selectedMessage.id }),
        message_type: 'reply'
      };
      
      // Validate required fields
      if (!replyData.name || !replyData.email || !replyData.subject || !replyData.message) {
        updateToError(loadingToast, 'Missing required information. Please ensure you are logged in.');
        return;
      }
      
      console.log('Sending reply data:', replyData);
      
      try {
        await userAPI.sendMessage(replyData);
        updateToSuccess(loadingToast, t('messages.reply.success', '✅ Reply sent successfully!'));
        setReplyText('');
        setShowReplyModal(false);
        setSelectedMessage(null);
        fetchAllData(); // Refresh all data
      } catch (replyError) {
        console.warn('Reply with extended data failed, trying simplified format...');
        
        // Fallback: try sending as a simple message without reply-specific fields
        const simpleReplyData = {
          name: userName,
          email: userEmail,
          phone: userData.phone || '',
          subject: getValidSubject(selectedMessage.subject), // Use valid subject choice
          message: `Reply to: ${selectedMessage.subject}\n\n${replyText.trim()}` // Include original subject in message
        };
        
        console.log('Trying simplified reply data:', simpleReplyData);
        
        try {
          await userAPI.sendMessage(simpleReplyData);
          updateToSuccess(loadingToast, t('messages.reply.success', '✅ Reply sent successfully!'));
          setReplyText('');
          setShowReplyModal(false);
          setSelectedMessage(null);
          fetchAllData(); // Refresh all data
        } catch (finalError) {
          throw finalError; // Let the outer catch handle this
        }
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      
      // Enhanced error handling
      let errorMessage;
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData.errors) {
          const fieldErrors = Object.values(errorData.errors).flat();
          errorMessage = fieldErrors.join(', ');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else {
          errorMessage = 'Invalid reply data. Please check the console for details.';
        }
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. Please log in and try again.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (!error.response) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = t('messages.failed_to_send', '❌ Failed to send reply');
      }
      
      updateToError(loadingToast, errorMessage);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'unread':
        return <FaEnvelope className="text-blue-500" />;
      case 'read':
        return <FaClock className="text-yellow-500" />;
      case 'replied':
        return <FaReply className="text-green-500" />;
      case 'closed':
        return <FaCheckCircle className="text-gray-500" />;
      default:
        return <FaEnvelope className="text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'unread':
        return 'bg-blue-100 text-blue-800';
      case 'read':
        return 'bg-yellow-100 text-yellow-800';
      case 'replied':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  // Combine messages and notifications into a unified list
  const getCombinedData = () => {
    const combinedData = [];
    
    // Add messages with type identifier (ensure messages is an array)
    const messageArray = Array.isArray(messages) ? messages : [];
    messageArray.forEach(message => {
      combinedData.push({
        ...message,
        type: 'message',
        created_at: message.created_at,
        sortDate: new Date(message.created_at)
      });
    });
    
    // Add notifications with type identifier (ensure notifications is an array)
    const notificationArray = Array.isArray(notifications) ? notifications : [];
    notificationArray.forEach(notification => {
      combinedData.push({
        ...notification,
        type: 'notification',
        subject: notification.title,
        message: notification.message,
        created_at: notification.created_at,
        sortDate: new Date(notification.created_at)
      });
    });
    
    // Sort by date (newest first)
    return combinedData.sort((a, b) => b.sortDate - a.sortDate);
  };

  // Filter data based on active tab
  const getFilteredData = () => {
    const combined = getCombinedData();
    switch (activeTab) {
      case 'messages':
        return combined.filter(item => item.type === 'message');
      case 'notifications':
        return combined.filter(item => item.type === 'notification');
      case 'driver-chat':
        return Array.isArray(activeBookings) ? activeBookings : [];
      default:
        return combined;
    }
  };

  const openDriverChat = (booking) => {
    setSelectedBooking(booking);
    setShowDriverChat(true);
  };

  const closeDriverChat = () => {
    setShowDriverChat(false);
    setSelectedBooking(null);
  };

  const handleSendMessageToDriver = async () => {
    if (!messageToDriver.trim()) {
      showError(t('messages.send_to_driver.empty_message', 'Please enter a message'));
      return;
    }

    setSendingMessage(true);
    const loadingToast = showLoading(t('messages.send_to_driver.sending', 'Sending message to driver...'));

    try {
      // Get user data
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const userEmail = localStorage.getItem('userEmail') || userData.email;
      const userName = userData.name || userData.username || 'User';

      if (!userEmail || !userName) {
        updateToError(loadingToast, t('messages.send_to_driver.login_required', 'Please log in to send messages'));
        return;
      }

      const messageData = {
        sender_name: userName,
        sender_email: userEmail,
        message: messageToDriver.trim(),
        message_type: 'user_to_driver'
      };

      await userAPI.sendMessageToDriver(messageData);
      
      updateToSuccess(loadingToast, t('messages.send_to_driver.success', '✅ Message sent to driver successfully!'));
      setMessageToDriver('');
      setShowSendMessageModal(false);
      
    } catch (error) {
      console.error('Error sending message to driver:', error);
      
      let errorMessage;
      if (error.response?.status === 404) {
        errorMessage = t('messages.send_to_driver.not_implemented', '⚠️ This feature is not yet available. The backend endpoint needs to be implemented.');
      } else if (error.response?.status === 400) {
        errorMessage = t('messages.send_to_driver.invalid_data', 'Invalid message data. Please try again.');
      } else if (error.response?.status === 403) {
        errorMessage = t('messages.send_to_driver.access_denied', 'Access denied. Please log in and try again.');
      } else if (error.response?.status >= 500) {
        errorMessage = t('messages.send_to_driver.server_error', 'Server error. Please try again later.');
      } else if (!error.response) {
        errorMessage = t('messages.send_to_driver.network_error', 'Network error. Please check your connection.');
      } else {
        errorMessage = t('messages.send_to_driver.failed', '❌ Failed to send message to driver');
      }
      
      updateToError(loadingToast, errorMessage);
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-yellow-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-yellow-400 border-t-transparent"></div>
          <p className="text-gray-600 mt-4">{t('messages.loading', 'Loading your messages...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-yellow-100 pt-20 pb-20">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Go back"
              >
                <FaArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">💬 {t('messages.view_messages', 'Your Messages & Notifications')}</h1>
                <p className="text-gray-600 mt-1">{t('messages.no_messages_desc', 'View messages from support and driver status updates')}</p>
              </div>
            </div>
            <button
              onClick={fetchAllData}
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
            >
              {t('messages.refresh', 'Refresh')}
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'all'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FaBell className="inline mr-2" />
              {t('messages.tabs.all', 'All')} ({getCombinedData().length})
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'messages'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FaComments className="inline mr-2" />
              {t('messages.support_messages', 'Support Messages')} ({messages.length})
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'notifications'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FaCar className="inline mr-2" />
              {t('messages.driver_updates', 'Driver Updates')} ({notifications.length})
            </button>
            <button
              onClick={() => setActiveTab('driver-chat')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'driver-chat'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FaComment className="inline mr-2" />
              {t('messages.driver_chat', 'Driver Chat')} ({activeBookings.length})
            </button>
          </div>
        </div>

        {/* Messages List */}
        {getFilteredData().length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{t('messages.no_messages', 'No messages yet')}</h3>
            <p className="text-gray-600 mb-6">{t('messages.no_messages_desc', 'You haven\'t received any messages from our support team yet.')}</p>
            
            {/* Show "Send a Message" button only in Support Messages tab */}
            {activeTab === 'messages' && (
              <button
                onClick={() => navigate('/contact')}
                className="px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-medium"
              >
                {t('messages.send_support_message', 'Send a Message')}
              </button>
            )}
            
            {/* Show "Send Message to Driver" button for other tabs */}
            {activeTab !== 'messages' && (
              <button
                onClick={() => setShowSendMessageModal(true)}
                className="px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-medium"
              >
                {t('messages.send_message', 'Send Message to Driver')}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {getFilteredData().map((item) => {
              // Handle driver chat bookings differently
              if (activeTab === 'driver-chat') {
                return (
                  <div
                    key={`booking-${item.id}`}
                    className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition-all border-l-4 border-yellow-500"
                    onClick={() => openDriverChat(item)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center">
                        <div className="mr-3">
                          <FaComment className="text-yellow-500" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-gray-800">
                              {t('chat.booking_with_driver', 'Chat with Driver')} - #{item.id}
                            </h3>
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                              {item.status || 'Active'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">
                            {item.pickup_location} → {item.dropoff_location}
                          </p>
                          <p className="text-xs text-gray-400">
                            {t('chat.driver', 'Driver')}: {item.driver_name || 'Assigned Driver'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-600">
                          {new Date(item.pickup_date).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-500">
                          {item.pickup_time}
                        </p>
                      </div>
                    </div>
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mt-3">
                      <p className="text-sm text-yellow-800">
                        <FaComment className="inline mr-2" />
                        {t('chat.click_to_chat', 'Click to start chatting with your driver')}
                      </p>
                    </div>
                  </div>
                );
              }
              
              // Regular message/notification display
              return (
                <div
                  key={`${item.type}-${item.id}`}
                  className={`bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition-all ${
                    item.type === 'notification' || item.status === 'unread' ? 'border-l-4 border-blue-500' : ''
                  }`}
                  onClick={() => setSelectedMessage(item)}
                >
                  <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <div className="mr-3">
                      {item.type === 'notification' ? (
                        <FaCar className="text-green-500" />
                      ) : (
                        getStatusIcon(item.status)
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-800">{item.subject}</h3>
                        {item.type === 'notification' && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            {t('messages.driver_update', 'Driver Update')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {new Date(item.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  {item.type === 'message' && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {t(`messages.status.${item.status}`, item.status.charAt(0).toUpperCase() + item.status.slice(1))}
                    </span>
                  )}
                </div>
                
                <p className="text-gray-700 mb-3 line-clamp-2">
                  {item.message}
                </p>
                
                {item.type === 'message' && item.admin_reply && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4">
                    <div className="flex items-center mb-2">
                      <FaReply className="text-yellow-600 mr-2" />
                      <span className="text-sm font-medium text-yellow-800">Admin Reply</span>
                    </div>
                    <p className="text-gray-700 text-sm">{item.admin_reply}</p>
                    {item.reply_date && (
                      <p className="text-xs text-gray-500 mt-2">
                        Replied on {new Date(item.reply_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}
                
                {item.type === 'notification' && item.booking && (
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
                    <div className="flex items-center mb-2">
                      <FaCar className="text-blue-600 mr-2" />
                      <span className="text-sm font-medium text-blue-800">{t('messages.modal.booking', 'Booking')} #{item.booking}</span>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}

        {/* Message Detail Modal */}
        {selectedMessage && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {getStatusIcon(selectedMessage.status)}
                    <div className="ml-3">
                      <h2 className="text-xl font-bold text-gray-800">{selectedMessage.subject}</h2>
                      <p className="text-sm text-gray-500">
                        {new Date(selectedMessage.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedMessage(null)}
                    className="text-gray-500 hover:text-gray-700 text-2xl p-1"
                  >
                    &times;
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('messages.modal.your_message', 'Your Message')}</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-line">{selectedMessage.message}</p>
                  </div>
                </div>

                {selectedMessage.admin_reply && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('messages.modal.admin_reply', 'Admin Reply')}</h3>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-gray-700 whitespace-pre-line">{selectedMessage.admin_reply}</p>
                      <p className="text-xs text-gray-500 mt-3">
                        {t('messages.modal.replied_on', 'Replied on')} {new Date(selectedMessage.reply_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setSelectedMessage(null)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    {t('messages.modal.close', 'Close')}
                  </button>
                  {selectedMessage.type === 'message' && selectedMessage.status !== 'closed' && (
                    <button
                      onClick={() => setShowReplyModal(true)}
                      className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                    >
                      {t('messages.modal.reply', 'Reply')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reply Modal */}
        {showReplyModal && (
          <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-800">{t('messages.reply.title', 'Reply to Message')}</h3>
              </div>
              
              <div className="p-6">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={t('messages.reply.placeholder', 'Type your reply here...')}
                  className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none"
                />
                
                <div className="flex justify-end space-x-3 mt-4">
                  <button
                    onClick={() => {
                      setShowReplyModal(false);
                      setReplyText('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    {t('messages.reply.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={handleSendReply}
                    disabled={!replyText.trim()}
                    className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('messages.reply.send_reply', 'Send Reply')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Send Message to Driver Modal */}
        {showSendMessageModal && (
          <div className="fixed inset-0 z-[70] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-800">
                    {t('messages.send_to_driver.title', '📨 Send Message to Driver')}
                  </h3>
                  <button
                    onClick={() => {
                      setShowSendMessageModal(false);
                      setMessageToDriver('');
                    }}
                    className="text-gray-500 hover:text-gray-700 text-2xl p-1"
                  >
                    &times;
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-4">
                    {t('messages.send_to_driver.description', 'Send a message that will be visible to drivers in their notification system.')}
                  </p>
                </div>
                
                <textarea
                  value={messageToDriver}
                  onChange={(e) => setMessageToDriver(e.target.value)}
                  placeholder={t('messages.send_to_driver.placeholder', 'Type your message to drivers here...')}
                  className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none"
                  disabled={sendingMessage}
                />
                
                <div className="flex justify-end space-x-3 mt-4">
                  <button
                    onClick={() => {
                      setShowSendMessageModal(false);
                      setMessageToDriver('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    disabled={sendingMessage}
                  >
                    {t('messages.send_to_driver.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={handleSendMessageToDriver}
                    disabled={!messageToDriver.trim() || sendingMessage}
                    className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {sendingMessage ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        {t('messages.send_to_driver.sending', 'Sending...')}
                      </>
                    ) : (
                      <>
                        📤 {t('messages.send_to_driver.send', 'Send Message')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Driver Chat Modal */}
        {showDriverChat && selectedBooking && (
          <DriverChat 
            booking={selectedBooking} 
            onClose={closeDriverChat} 
          />
        )}
      </div>
    </div>
  );
};

export default UserMessages;
